<script>
	import thumb_8IhNB6JU3so from '$lib/Components/Youtube/images/8IhNB6JU3so.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Parallax from '$lib/Components/Parallax/Parallax.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import CommercialForm from '$lib/PageSections/CommercialForm/CommercialForm.svelte';
	import NoiseReductionComparison from '$lib/PageSections/DataDrivenResults/NoiseReductionComparison.svelte';
	import FeaturedArticles from '$lib/PageSections/FeaturedArticles/FeaturedArticles.svelte';

	import heatmap from './images/heatmap.jpg';
	import image from './images/image.png';
	import multifamily from './images/Multifamily.png';
	import officeParallax from './images/office-parallax.png?w=1200;1000;800;600;400;';
	import officeImage from './images/office.jpg';
	import preheat from './images/preheatmap.jpg';
</script>

<Section backgroundColor="white">
	<h2>Block Drafts and Noise in the Office</h2>
	<p>
		Facility managers know the value of office soundproofing. They're the ones
		tenants and employees complain to when outside noise like construction,
		trains or traffic makes it hard to concentrate on work. Since most noise
		enters through the windows, it's easy to reduce it – and block cold drafts –
		with window inserts. Read on to learn how to soundproof an office with Indow
		inserts.
	</p>
	<Image
		src={officeImage}
		alt="A beautiful looking building."
		loading="eager"
	/>
</Section>

<Section backgroundColor="white">
	<h2>Contactless Office Soundproofing</h2>
	<p>
		There are many <a
			href="https://www.workdesign.com/2019/05/good-office-design-includes-soundproofing-for-productivity/"
			target="_blank"
			rel="noreferrer">noise reduction solutions</a
		> you can use in the office. If you have an office space you can't redesign or
		request major improvements on, window inserts are one of the most impactful and
		least intrusive solutions you can invest in. And they can be installed in hours.
	</p>
	<p>
		Indow window inserts press into the inside of windows to add insulation
		against noise. The compression tubing creates a tight seal, blocking sound
		from entering. The high-grade acrylic panels are better at absorbing sound
		than glass. The seal also creates a dead air space between the panel and the
		window, insulates further against sound.
	</p>
	<p>
		All of these combined can reduce noise up to 50% – 70% depending on the type
		of windows they are added to. The inserts are an easily removable upgrade
		that causes no harm to the frame or surrounding walls – a great benefit for
		those leasing an office space.
	</p>
	<Bubble title="Office Noise Reduction Solutions & Benefits:">
		<ul>
			<li>Eliminate distracting noise for staff and office visitors.</li>
			<li>Stop the need for space heaters by increasing thermal comfort.</li>
			<li>Average 20 percent savings on energy bills.</li>
			<li>Create a greener office.</li>
		</ul>
	</Bubble>
</Section>

<Section backgroundColor="white">
	<h2>Soundproofing Improves Employee Happiness</h2>
	<p>
		Immersion Active's employees knew they needed office noise reduction
		solutions when they would have to pause conference calls as fire trucks or
		other traffic drove by their Frederick, Maryland building. The noise came
		straight through the single-pane windows, which was an embarrassment and a
		waste of time. Then they found Indow window inserts.
	</p>
	<p>
		“Now people gravitate to those rooms because they're so quiet compared to
		the other rooms,” said co-founder and president David Weigelt.
	</p>
	<p>
		Commercial window inserts are available in Acoustic Grade, which reduce
		outside noise by up to 70% when placed over single-pane windows. When placed
		over operable double-pane windows, the inserts reduce noise by 12 dBA,
		equivalent to a 50% reduction in noise and an <a
			href="/static/pdfs/Indow_Field-Sound-Transmission-Loss_081116.pdf"
			>STC rating of 42-45.</a
		>*
	</p>
	<Youtube posterImageSrc={thumb_8IhNB6JU3so} videoId="8IhNB6JU3so" />
</Section>

<Section backgroundColor="white">
	<h2>Instant Employee Happiness in the Workplace</h2>
	<p>
		The Ballston Tower's tenants started to increasingly complain about
		construction noise during the redevelopment of the Ballston Mall in
		Arlington, Virginia. Then the property manager installed Indow inserts.
	</p>
	<p>
		“All the complaints we were getting, stopped,” said Jacob Sickels. “From our
		point of view, they worked perfectly.”
	</p>
	<p>
		Uncomfortable employees are distracted employees. With their airtight seal,
		Indow inserts create a space conducive to concentration.
	</p>
</Section>
<NoiseReductionComparison titleString={null} />

<Section backgroundColor="white">
	<h2>The ROI of Saying “No” to Window Replacement</h2>
	<p>
		Arlington County government in Virginia used Indow inserts to convert an
		office building with fixed windows into a homeless shelter. Replacing the
		windows would have cost more than $1 million and required exterior
		scaffolding. For less than 20% of that, the county installed 304 window
		inserts and said goodbye to plastic sheeting and expensive space heaters
		that are dangerous if left on. The 50-year-old single-pane windows instantly
		performed like new double-panes.
	</p>
	<Image
		src={multifamily}
		alt="A nice looking office building during the daytime."
	/>
	<p>
		Don't take our word for how well these inserts insulate. The thermal imaging
		photos below show what they can do for your building. A <a
			href="https://www.pnnl.gov/main/publications/external/technical_reports/PNNL-22855.pdf"
			rel="external">U.S. Department of Energy study</a
		>
		found Indow inserts reduced energy use in a Seattle home by 20 percent.
	</p>
	<Grid as="div" class="w-[95%]" columns={2} rows={1} center>
		<Image
			src={preheat}
			alt="This is a building before it is enhanced via thermal layering."
		/>
		<Image
			src={heatmap}
			alt="This is the same building with a thermal layer applied."
		/>
	</Grid>
</Section>

<Section backgroundColor="white">
	<h2>Office Soundproofing Increases Market Value</h2>
	<p>
		Before Foto Crush agreed to rent space in the Olympic Mills building in
		Portland, the marketing firm asked the property manager to soundproof the
		office to block the train noise. Instead of replacing the windows, Mike
		Larkin installed Indow window inserts as their office noise reduction
		solution, which also made the space more comfortable by eliminating drafts.
	</p>
	<p>
		“We realized that if we fixed our window problem we could lease the space
		for market rates… instead of leasing far below market,” said the Elliott
		Associates broker and property manager. “The return on the investment has
		been outstanding – and we like happy tenants.”
	</p>
	<Image src={image} alt="A glowing review powered by Reviews.io" />
	<p>
		Indow inserts are the alternative to replacement windows that make your
		office space more worker-friendly, cost-effective and energy efficient. We
		have a <a href="/commercial-storm-windows/pilot-program">pilot program</a> to
		test our window inserts in one room of your building. To check for their availability
		in your area, enter your zip code in the form below.
	</p>
</Section>
<CommercialForm focus={{ sound: true }} id="free-consultation" />
<Parallax image={officeParallax} />
<Awards />
<FeaturedArticles />
